/**
 * Phase 22 Plan 22-03 — ClientsListPage
 *
 * Route: /clients
 * Permission gate: clients.view
 *
 * Paginated list with search + status filter.
 * Row actions: Edit, Deactivate (active) or Reactivate (inactive).
 * Mirrors WorkOrdersListPage / SitesListPage pattern.
 */

import { PlusIcon } from '@phosphor-icons/react';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { DeactivateClientConfirmDialog } from '@/components/clients/DeactivateClientConfirmDialog';
import { ReactivateClientDialog } from '@/components/clients/ReactivateClientDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useClientsQuery } from '@/hooks/useClientQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import type { ClientListItemDto } from '@/types/client';

const PAGE_SIZE = 20;

type StatusFilter = 'active' | 'inactive' | 'all';

function isValidStatus(s: string | null): s is StatusFilter {
  return s === 'active' || s === 'inactive' || s === 'all';
}

export function ClientsListPage(): JSX.Element {
  const { permissions } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQ = searchParams.get('q') ?? '';
  const statusParam = searchParams.get('status');
  const initialStatus: StatusFilter = isValidStatus(statusParam) ? statusParam : 'active';
  const initialPage = (() => {
    const p = Number.parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  })();

  const [q, setQ] = useState<string>(initialQ);
  const [debouncedQ, setDebouncedQ] = useState<string>(initialQ);
  const [status, setStatus] = useState<StatusFilter>(initialStatus);
  const [page, setPage] = useState<number>(initialPage);

  const [deactivateTarget, setDeactivateTarget] = useState<ClientListItemDto | null>(null);
  const [reactivateTarget, setReactivateTarget] = useState<ClientListItemDto | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => { setDebouncedQ(q); }, 300);
    return () => { window.clearTimeout(t); };
  }, [q]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQ !== '') next.set('q', debouncedQ);
    if (status !== 'active') next.set('status', status);
    if (page !== 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [debouncedQ, status, page, setSearchParams]);

  useEffect(() => { setPage(1); }, [debouncedQ, status]);

  const clientsQuery = useClientsQuery({
    ...(debouncedQ !== '' ? { q: debouncedQ } : {}),
    status,
    page,
    pageSize: PAGE_SIZE,
  });

  const canCreate = permissions.includes('clients.create');
  const canEdit = permissions.includes('clients.edit');
  const canDelete = permissions.includes('clients.delete');

  const columns = useMemo<ColumnDef<ClientListItemDto>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/clients/${String(row.original.id)}`}
            className="text-primary hover:underline font-medium"
            data-testid={`client-row-name-${String(row.original.id)}`}
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: 'code',
        header: 'Code',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.code}</span>
        ),
      },
      {
        accessorKey: 'contactName',
        header: 'Contact',
        cell: ({ row }) => row.original.contactName ?? '—',
      },
      {
        accessorKey: 'contactEmail',
        header: 'Email',
        cell: ({ row }) => row.original.contactEmail ?? '—',
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => (
          <span
            className={row.original.isActive ? 'text-green-600' : 'text-muted-foreground'}
            data-testid={`client-status-${String(row.original.id)}`}
          >
            {row.original.isActive ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link
                to={`/clients/${String(row.original.id)}`}
                data-testid={`client-row-view-${String(row.original.id)}`}
              >
                View
              </Link>
            </Button>
            {canEdit && (
              <Button asChild variant="outline" size="sm">
                <Link
                  to={`/clients/${String(row.original.id)}/edit`}
                  data-testid={`client-row-edit-${String(row.original.id)}`}
                >
                  Edit
                </Link>
              </Button>
            )}
            {canDelete && row.original.isActive && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => { setDeactivateTarget(row.original); }}
                data-testid={`client-row-deactivate-${String(row.original.id)}`}
              >
                Deactivate
              </Button>
            )}
            {canDelete && !row.original.isActive && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setReactivateTarget(row.original); }}
                data-testid={`client-row-reactivate-${String(row.original.id)}`}
              >
                Reactivate
              </Button>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissions],
  );

  const data = clientsQuery.data?.items ?? [];
  const total = clientsQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const tableData = data as unknown as ClientListItemDto[];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!permissions.includes('clients.view')) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Clients</h1>
          <p className="text-muted-foreground">
            Manage clients and their linked work orders.
          </p>
        </div>
        {canCreate && (
          <Button asChild data-testid="client-add-button">
            <Link to="/clients/new">
              <PlusIcon size={16} weight="bold" /> New Client
            </Link>
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Clients</CardTitle>
          <CardDescription>Search by name or code. Filter by status.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px] space-y-1">
              <Label htmlFor="client-search">Search</Label>
              <Input
                id="client-search"
                value={q}
                onChange={(e) => { setQ(e.target.value); }}
                placeholder="Name or code…"
                data-testid="client-search-input"
              />
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="client-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => { if (isValidStatus(v)) setStatus(v); }}
              >
                <SelectTrigger id="client-status" data-testid="client-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {clientsQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="clients-loading">
              Loading clients…
            </p>
          )}
          {clientsQuery.isError && (
            <p className="text-sm text-destructive" data-testid="clients-error">
              Failed to load clients.
            </p>
          )}

          {clientsQuery.data && (
            <>
              <div className="overflow-x-auto">
                <Table data-testid="clients-table">
                  <TableHeader>
                    {table.getHeaderGroups().map((hg) => (
                      <TableRow key={hg.id}>
                        {hg.headers.map((h) => (
                          <TableHead key={h.id}>
                            {h.isPlaceholder
                              ? null
                              : flexRender(h.column.columnDef.header, h.getContext())}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={columns.length} className="text-center py-8">
                          <span data-testid="clients-empty">No clients found.</span>
                        </TableCell>
                      </TableRow>
                    ) : (
                      table.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          data-testid={`client-row-${String(row.original.id)}`}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-muted-foreground" data-testid="clients-pagination-summary">
                  Page {page} of {pageCount} • {total} total
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { setPage((p) => Math.max(1, p - 1)); }}
                    disabled={page <= 1}
                    data-testid="clients-pagination-prev"
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { setPage((p) => Math.min(pageCount, p + 1)); }}
                    disabled={page >= pageCount}
                    data-testid="clients-pagination-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {deactivateTarget !== null && (
        <DeactivateClientConfirmDialog
          open={true}
          onOpenChange={(o) => { if (!o) setDeactivateTarget(null); }}
          clientId={deactivateTarget.id}
          clientName={deactivateTarget.name}
        />
      )}
      {reactivateTarget !== null && (
        <ReactivateClientDialog
          open={true}
          onOpenChange={(o) => { if (!o) setReactivateTarget(null); }}
          clientId={reactivateTarget.id}
          clientName={reactivateTarget.name}
        />
      )}
    </div>
  );
}
